import { useCallback, useEffect, useState } from "react";
import * as React from "react";

import { css } from "@emotion/react";
import BoxContainer from "@hl/base-components/lib/BoxContainer";
import { ShareArrowUp } from "@hl/base-components/lib/assets/icons.generated";
import { SECONDARY_COLOR } from "@hl/base-components/lib/theme/button";
import { useModalStack } from "@hl/shared-features/lib/features/modal";
import { TX_COMPLETE_EVENT } from "@hl/shared-features/lib/features/tx-history/TxUpdater";
import { networkLookup } from "@hl/shared-features/lib/utils/blockExplorer";
import { Button, CloseButton, Group, Loader, Stack, Text } from "@mantine/core";

import { ReactComponent as CheckIcon } from "assets/icons/check-green.svg";
import ShareSaleModal from "~/features/tokens/sections/ManageToken/modals/ShareSaleModal";

export const DeployStatusBox = ({
  isDeploying,
  collectionId,
  mintUrl,
  chainId,
  isAuction,
}: {
  isDeploying: boolean;
  collectionId?: string;
  mintUrl: string;
  chainId: number;
  isAuction: boolean;
}) => {
  const [showDeployComplete, setShowDeployComplete] = useState<boolean>(false);
  const [isDeployingInner, setIsDeployingInner] =
    useState<boolean>(isDeploying);
  const { pushModal } = useModalStack();

  const shareSaleOnClick = useCallback(
    () =>
      pushModal(
        <ShareSaleModal
          data={{
            entity: isAuction ? "auction" : "mint",
            mintUrl,
            chainId,
          }}
        />,
        {
          size: "lg",
          title: <Text>`Share ${isAuction ? "auction" : "mint"} page`</Text>,
        }
      ),
    [mintUrl]
  );

  const handleMintComplete = useCallback(
    (e: Event) => {
      const eventCollectionId = (e as CustomEvent).detail;
      if (collectionId === eventCollectionId) {
        setShowDeployComplete(true);
        setIsDeployingInner(false);
      }
    },
    [collectionId]
  );

  useEffect(() => {
    document.addEventListener(TX_COMPLETE_EVENT, handleMintComplete);
    return () => {
      document.removeEventListener(TX_COMPLETE_EVENT, handleMintComplete);
    };
  }, [handleMintComplete]);

  const networkData = networkLookup(chainId);

  if (showDeployComplete) {
    return (
      <BoxContainer p="md">
        <Stack align="center" spacing="xs">
          <Group position="right" w="100%">
            <Group position="apart" w="50%">
              <CheckIcon
                css={css`
                  height: 32px;
                  width: 32px;
                `}
              />
              {/*@ts-ignore*/}
              <CloseButton onClick={() => setShowDeployComplete(false)} />
            </Group>
          </Group>
          <Text align="center" size="xl" weight={500}>
            Successfully deployed on {networkData.displayName}
          </Text>
          {!isAuction ? (
            <Text align="center" size="sm" px={80}>
              Congrats! Your collection has been deployed on the{" "}
              {networkData.displayName} blockchain. It is now available for
              people to mint on the mint page. Share the mint page with your
              collectors and fans.
            </Text>
          ) : (
            <Text>
              Congrats! Your auction has been deployed on the{" "}
              {networkData.displayName} blockchain. It is now available for
              people to bid on. Share the auction page with your collectors and
              fans.
            </Text>
          )}
          <Button
            size="xl"
            fullWidth
            color={SECONDARY_COLOR}
            leftIcon={<ShareArrowUp />}
            onClick={shareSaleOnClick}
          >
            {!isAuction ? "Share mint page" : "Share auction page"}
          </Button>
        </Stack>
      </BoxContainer>
    );
  } else if (isDeployingInner && !showDeployComplete) {
    return (
      <BoxContainer>
        <Stack align="center" px={96} spacing="xs">
          <Loader aria-label="loading" size={32} />
          <Text align="center" size="xl" weight={500}>
            Deploying on {networkData.displayName}...
          </Text>
          <Text align="center" size="sm">
            We&apos;re currently deploying your collection to{" "}
            {networkData.displayName}. We&apos;ll let you know when it&apos;s
            complete.
          </Text>
        </Stack>
      </BoxContainer>
    );
  } else {
    return <></>;
  }
};
